import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoginForm } from '@/components/login-form'
import { LoginHero } from '@/components/login-hero'
import { authClient } from '@/lib/auth-client'
import { peekReturnTo, safeReturnTo, takeReturnTo } from '@/lib/return-to'
import { hasSessionIndicator } from '@/lib/session-indicator'
import { pendingPlanReturnTo } from '@/store/onboarding.store'

export const Route = createFileRoute('/auth/login')({
  head: () => ({
    meta: [{ title: 'Sign in — GoalTree' }],
  }),
  validateSearch: (search): { returnTo?: string } => ({
    returnTo: safeReturnTo(search.returnTo),
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const navigate = useNavigate()
  const { returnTo } = Route.useSearch()

  // A cross-domain Google sign-in can route the return through THIS page while the
  // one-time-token → session exchange is still resolving. The `oauthPending` marker
  // (set right before the Google redirect) means "auth is in flight" — honor the
  // same grace window `_authenticated.tsx` uses: wait it out here instead of clearing
  // it and flashing the logged-out form, which was dropping the first attempt.
  const [oauthPending, setOauthPending] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('oauthPending') === '1',
  )

  // The account-deletion marker is unrelated to auth-in-flight — safe to clear on mount.
  useEffect(() => {
    sessionStorage.removeItem('accountDeleting')
  }, [])

  // Clear the OAuth marker once auth resolves, or after a grace period if the
  // exchange never completes (cancelled / failed) so we don't wait forever.
  useEffect(() => {
    if (isAuthenticated) {
      sessionStorage.removeItem('oauthPending')
      setOauthPending(false)
    }
  }, [isAuthenticated])
  useEffect(() => {
    if (!oauthPending) return
    const timer = setTimeout(() => {
      sessionStorage.removeItem('oauthPending')
      setOauthPending(false)
    }, 12_000)
    return () => clearTimeout(timer)
  }, [oauthPending])

  // Google usually lands on /today directly; this route also forwards an already-
  // signed-in visitor (magic-link tab) or a cross-domain OAuth return on to the app.
  useEffect(() => {
    if (isAuthenticated)
      navigate({ to: takeReturnTo(returnTo) ?? pendingPlanReturnTo() ?? '/today', replace: true })
  }, [isAuthenticated, navigate, returnTo])

  // Detect a sign-in completed in ANOTHER context — the common case being a magic-
  // link email opened in the browser while the user waits in the installed PWA
  // (desktop PWAs share Chrome's cookie jar, so that session is valid here too).
  // better-auth won't refetch on its own (refetchOnWindowFocus is off), so poll:
  // the moment getSession sees the session we forward on — /today then routes a new
  // user to onboarding. Without this the PWA sits on the login form forever, even
  // after the link is clicked. Re-checks immediately on focus.
  useEffect(() => {
    if (isAuthenticated) return
    let cancelled = false
    const check = async () => {
      if (cancelled || document.visibilityState === 'hidden') return
      try {
        const res = await authClient.getSession({ query: { disableCookieCache: true } })
        if (cancelled) return
        if (res?.data) {
          navigate({
            to: takeReturnTo(returnTo) ?? pendingPlanReturnTo() ?? '/today',
            replace: true,
          })
          return
        }
        // No session in place but a session indicator says we were signed in on
        // this device (a cold resume that landed on the login page): the client is
        // stale — a full reload re-bootstraps it. Once only, so a genuine logout
        // still shows the form.
        if (hasSessionIndicator() && sessionStorage.getItem('authReloadTried') !== '1') {
          sessionStorage.setItem('authReloadTried', '1')
          window.location.reload()
        }
      } catch {
        /* keep polling */
      }
    }
    const id = setInterval(check, 3000)
    const onFocus = () => void check()
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      cancelled = true
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [isAuthenticated, navigate, returnTo])

  // Once we've resolved to "logged out" and shown the form, stay on the form —
  // don't flash the spinner again when auth momentarily reloads on tab refocus.
  const resolvedRef = useRef(false)
  if (!isLoading && !isAuthenticated && !oauthPending) resolvedRef.current = true

  // Clicking a magic-link email — or returning from a cross-domain OAuth — lands
  // here with the session still resolving. Show a spinner (rather than flashing the
  // login form or a blank gap) while auth loads, we redirect, or OAuth is in flight.
  if (!resolvedRef.current && (isLoading || isAuthenticated || oauthPending)) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      <LoginHero className="mb-5 lg:hidden" />
      <LoginForm
        returnTo={returnTo ?? peekReturnTo()}
        signingIn={false}
        title={t('login.heroTitle')}
        subtitle={t('login.heroSubtitle')}
      />
    </>
  )
}
